import { Column, Index, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { MPaymentTransactionEntity } from './m-payment-transaction.entity';
import { OrderEntity } from './order.entity';

@Index('m_sm_payment_fk_client__id', ['mSmPaymentFkClientId'], {})
@Entity('m_payment_payment_method', {}, { label: 'Payment Method' })
export class MPaymentPaymentMethodEntity extends BaseEntity {
	@Column('varchar', {
		name: 'm_payment_externalId_paymentProcessor',
		nullable: true,
		length: 64,
	})
	public mPaymentExternalIdPaymentProcessor!: string | null;

	@Column('varchar', {
		name: 'm_payment_externalId_erp',
		nullable: true,
		length: 64,
	})
	public mPaymentExternalIdErp!: string | null;

	@Column('char', { name: 'm_payment_lastFour', nullable: true, length: 4 })
	public mPaymentLastFour!: string | null;

	@Column('char', {
		name: 'm_payment_routingNumber',
		nullable: true,
		length: 9,
	})
	public mPaymentRoutingNumber!: string | null;

	@Column('varchar', { name: 'm_payment_type', length: 64 })
	public mPaymentType!: string;

	/**
	 * The card BRAND — Visa, MasterCard, Amex — or the bank-account kind for
	 * ACH. It says nothing about how a card is funded: a Visa here may be
	 * credit, debit or prepaid. For the surcharge decision read
	 * `mPaymentCardCategory` below, never this.
	 */
	@Column('varchar', { name: 'm_payment_subtype', nullable: true, length: 64 })
	public mPaymentSubtype!: string | null;

	/**
	 * PD-15142 — how this card is funded: `credit`, `debit`, `prepaid`,
	 * `charge`, `deferred_debit`, `unknown` or `unavailable`. Null for ACH and
	 * for cards stored before this was captured.
	 *
	 * This decides whether a processing fee may be charged at all. Regulation II
	 * prohibits surcharging debit — including signature debit run as credit —
	 * and the networks extend that to prepaid, so ONLY `credit` is
	 * surchargeable. Anything unresolved gets no fee: absorbing 3% is a cost,
	 * while surcharging a debit card is a federal violation, so the default has
	 * to be the safe one.
	 *
	 * Resolved from the gateway at three points, so a card cannot slip through
	 * whichever way it was created: at card entry (Collect.js reports the
	 * category while the customer is still typing), lazily before first use if
	 * still unset, and by a one-off backfill for cards that predate this.
	 */
	@Column('varchar', {
		name: 'm_payment_cardCategory',
		nullable: true,
		length: 32,
	})
	public mPaymentCardCategory!: string | null;

	/**
	 * When the category above was last resolved from the gateway. Lets the
	 * backfill and the lazy resolver tell "never asked" from "asked, and the
	 * gateway had no answer", and gives an audit trail for a surcharge decision
	 * that has to be defensible to a card network.
	 */
	@Column('datetime', {
		name: 'm_payment_cardCategoryResolvedDate',
		nullable: true,
	})
	public mPaymentCardCategoryResolvedDate!: Date | null;

	@Column('varchar', { name: 'm_payment_name', nullable: true, length: 255 })
	public mPaymentName!: string | null;

	@Column('varchar', {
		name: 'm_payment_address1',
		nullable: true,
		length: 128,
	})
	public mPaymentAddress1!: string | null;

	@Column('varchar', {
		name: 'm_payment_address2',
		nullable: true,
		length: 128,
	})
	public mPaymentAddress2!: string | null;

	@Column('varchar', { name: 'm_payment_city', nullable: true, length: 64 })
	public mPaymentCity!: string | null;

	@Column('varchar', { name: 'm_payment_state', nullable: true, length: 16 })
	public mPaymentState!: string | null;

	@Column('varchar', { name: 'm_payment_zip', nullable: true, length: 16 })
	public mPaymentZip!: string | null;

	@Column('varchar', { name: 'm_payment_country', nullable: true, length: 16 })
	public mPaymentCountry!: string | null;

	@Column('tinyint', {
		name: 'm_payment_isPrimary',
		width: 1,
		default: () => "'0'",
	})
	public mPaymentIsPrimary!: boolean;

	@Column('int', { name: 'm_sm_payment_fk_client__id', unsigned: true })
	public mSmPaymentFkClientId!: number;

	@Column('date', { name: 'm_payment_expirationDate', nullable: true })
	public mPaymentExpirationDate!: string | null;

	@ManyToOne(() => ClientEntity, (client) => client.paymentMethods, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([
		{ name: 'm_sm_payment_fk_client__id', referencedColumnName: 'id' },
	])
	public mSmPaymentFkClient!: ClientEntity;

	@OneToMany(
		() => MPaymentTransactionEntity,
		(mPaymentTransaction) =>
			mPaymentTransaction.mPaymentFkMPaymentPaymentMethod,
		{},
	)
	public mPaymentTransactions!: Array<MPaymentTransactionEntity>;

	@OneToMany(() => OrderEntity, (order) => order.mSmPaymentFkMPaymentMethod, {})
	public orders!: Array<OrderEntity>;
}
